import { POINT_OVERLAY_PADDING } from '../../../../core/components/data-point/constants.js';
import type { PointShapeType } from '../../../../core/components/shape/type.js';
import type { DataPoint } from '../../../../core/types/data-point.js';
import { safeGet } from '../../../../core/utils/safe-get.js';
import type { AxisIdToScales } from '../../../context/XYChartScales.context.js';
import {
  DOT_ID,
  DOT_VARIANT,
  LINE_ID,
  LINE_VARIANT,
  AREA_ID,
  AREA_VARIANT,
  BAND_VARIANT,
  type AnyXYChartScale,
  type AxisIdToScaleDomain,
  type DotMetadata,
  type DotSeriesWithDatapoints,
  type LineMetadata,
  type LineSeriesWithDatapoints,
  type AreaMetadata,
  type AreaSeriesWithDatapoints,
  type MapDatapoints,
} from '../../../types/xy-chart-internals.js';
import type { XYChartSelectionLinearMetadata } from '../../../types/xy-chart-selection.js';
import { getLinearAccessors } from '../../../utils/get-linear-accessors.js';
import { getCurrentScale } from '../../../utils/get-scale.js';
import { relAccessor } from '../../../utils/value-representation.js';
import {
  bandX0Accessor,
  bandX1Accessor,
  bandY0Accessor,
  bandY1Accessor,
} from '../../displays/band-accessors.js';

type AnySeries =
  | DotSeriesWithDatapoints
  | LineSeriesWithDatapoints
  | AreaSeriesWithDatapoints;
type AnyMetadata = DotMetadata | LineMetadata | AreaMetadata;

type MetadataGetter =
  | Map<AnySeries, AnyMetadata>
  | ((series: AnySeries) => AnyMetadata | undefined);

interface HighlightedLinearPoint extends DataPoint {
  shape?: PointShapeType;
}

function getYRangeMinMax(range: number[]) {
  let min = range[0];
  let max = range[0];
  for (let i = 1; i < range.length; i++) {
    const v = range[i];
    if (v < min) {
      min = v;
    } else if (v > max) {
      max = v;
    }
  }
  return min < max ? [min, max] : [max, min];
}

function resolveMetadata(
  getter: MetadataGetter,
  series: AnySeries,
): AnyMetadata | undefined {
  if (typeof getter === 'function') {
    return getter(series);
  }
  return safeGet(getter as Map<AnySeries, AnyMetadata>, series);
}

const idMap: Record<string, string> = {
  [DOT_VARIANT]: DOT_ID,
  [LINE_VARIANT]: LINE_ID,
  [AREA_VARIANT]: AREA_ID,
};

/**
 * Serializes a band area series point into one or two overlay highlight points —
 * one per band endpoint (x0/x1 for horizontal bands, y0/y1 for vertical bands).
 *
 * Each endpoint is filtered independently: on a zoomed chart a band may have
 * one endpoint inside the visible range and one outside, and we still want to
 * render the indicator for the visible one.
 */
function serializeBandArea(
  meta: AreaMetadata,
  point: XYChartSelectionLinearMetadata,
  xScale: AnyXYChartScale,
  yScale: AnyXYChartScale,
  xRelAcc: ((value: number) => number) | undefined,
  yRelAcc: ((value: number) => number) | undefined,
  yMin: number,
  yMax: number,
): HighlightedLinearPoint[] | null {
  const coords =
    meta.orientation === 'horizontal'
      ? [
          {
            x: bandX0Accessor(xScale, xRelAcc)(point.datapoint),
            y: bandY0Accessor(yScale, yRelAcc)(point.datapoint),
          },
          {
            x: bandX1Accessor(xScale, xRelAcc)(point.datapoint),
            y: bandY0Accessor(yScale, yRelAcc)(point.datapoint),
          },
        ]
      : [
          {
            x: bandX0Accessor(xScale, xRelAcc)(point.datapoint),
            y: bandY0Accessor(yScale, yRelAcc)(point.datapoint),
          },
          {
            x: bandX0Accessor(xScale, xRelAcc)(point.datapoint),
            y: bandY1Accessor(yScale, yRelAcc)(point.datapoint),
          },
        ];

  const visiblePoints: HighlightedLinearPoint[] = coords
    .filter(({ y }) => y >= yMin && y <= yMax)
    .map(({ x, y }) => ({
      id: AREA_ID,
      x: x + POINT_OVERLAY_PADDING,
      y: y + POINT_OVERLAY_PADDING,
      color: meta.color,
      shape: undefined,
    }));

  return visiblePoints.length > 0 ? visiblePoints : null;
}

export const serializeLinearSeriesValues =
  (
    linearMetadata: MetadataGetter,
    axisIdToScales: AxisIdToScales,
    axisIdToScaleDomain: AxisIdToScaleDomain,
    datapointMetadataMap: MapDatapoints,
  ) =>
  (
    point: XYChartSelectionLinearMetadata,
  ): HighlightedLinearPoint | HighlightedLinearPoint[] | null => {
    const meta = resolveMetadata(linearMetadata, point.series);

    if (!meta) {
      return null;
    }

    const xScale = getCurrentScale(axisIdToScales, point.series.xAxisId);
    const yScale = getCurrentScale(axisIdToScales, point.series.yAxisId);

    if (!xScale || !yScale) {
      return null;
    }

    const { xAxis, yAxis } = meta;
    const xRelAccessor = relAccessor(xAxis, axisIdToScaleDomain);
    const yRelAccessor = relAccessor(yAxis, axisIdToScaleDomain);

    const { xAccessor, yAccessor } = getLinearAccessors(
      point.series.type,
      meta,
      datapointMetadataMap,
      xRelAccessor,
      yRelAccessor,
    );

    const yBase = yAccessor(yScale)(point.datapoint);

    const [yMin, yMax] = getYRangeMinMax(yScale.range());
    if (yBase < yMin || yBase > yMax) {
      return null;
    }

    const areaMeta =
      point.series.type === AREA_VARIANT ? (meta as AreaMetadata) : undefined;

    if (areaMeta?.variant === BAND_VARIANT) {
      return serializeBandArea(
        areaMeta,
        point,
        xScale,
        yScale,
        xRelAccessor,
        yRelAccessor,
        yMin,
        yMax,
      );
    }

    const xPos = xAccessor(xScale)(point.datapoint);

    return {
      id: idMap[point.series.type],
      x: xPos + POINT_OVERLAY_PADDING,
      y: yBase + POINT_OVERLAY_PADDING,
      color: meta.color,
      // shape is only defined for dot series
      shape:
        point.series.type === DOT_VARIANT
          ? (meta as DotMetadata).shape
          : undefined,
    };
  };
